import { DEMO_CONFIG, clampZoom } from '@maya/config';
export const AUTO_ZOOM_DEFAULTS = Object.freeze({ ...DEMO_CONFIG.autoZoom, normalZoom: DEMO_CONFIG.zoom.initial });
/** Range (max - min) of the first-seen path over the trailing window, in band heights; undefined until minSamples. */
export function trailingRangeBands(points, bandHeight, windowMs, minSamples) {
    const latest = points.at(-1);
    if (!latest || !(bandHeight > 0))
        return undefined;
    let low = Infinity, high = -Infinity, count = 0;
    for (let i = points.length - 1; i >= 0; i--) {
        const p = points[i];
        if (p.sourceTimeMs < latest.sourceTimeMs - windowMs)
            break;
        if (!Number.isFinite(p.value))
            continue;
        low = Math.min(low, p.value);
        high = Math.max(high, p.value);
        count++;
    }
    return count >= minSamples ? (high - low) / bandHeight : undefined;
}
const smoothstep = (u) => u * u * (3 - 2 * u);
/**
 * Presentation-only zoom that follows how much the market moves. Hysteresis: a regime is entered at one threshold
 * and left at a wider one, and is held at least minDwellMs. Any manual zoom disables it for the session.
 */
export class AutoZoomController {
    settings;
    enabled = true;
    regime = 'normal';
    changedAtMs = -Infinity;
    transition;
    constructor(settings = AUTO_ZOOM_DEFAULTS) {
        this.settings = settings;
    }
    /** Classifies the trailing range; returns the (possibly unchanged) regime. */
    observe(rangeBands, nowMs) {
        if (!this.enabled || rangeBands === undefined || !Number.isFinite(rangeBands) || nowMs - this.changedAtMs < this.settings.minDwellMs)
            return this.regime;
        const s = this.settings;
        let next = this.regime;
        if (this.regime === 'quiet') {
            if (rangeBands > s.quietExitBands)
                next = rangeBands > s.busyEnterBands ? 'busy' : 'normal';
        }
        else if (this.regime === 'busy') {
            if (rangeBands < s.busyExitBands)
                next = rangeBands < s.quietEnterBands ? 'quiet' : 'normal';
        }
        else if (rangeBands < s.quietEnterBands)
            next = 'quiet';
        else if (rangeBands > s.busyEnterBands)
            next = 'busy';
        if (next !== this.regime) {
            this.regime = next;
            this.changedAtMs = nowMs;
        }
        return this.regime;
    }
    target() { return clampZoom(this.regime === 'quiet' ? this.settings.quietZoom : this.regime === 'busy' ? this.settings.busyZoom : this.settings.normalZoom); }
    /** The zoom to show now, eased over easeMs from where the chart was when the target changed; undefined when at rest. */
    step(current, nowMs, easeMs = this.settings.easeMs) {
        if (!this.enabled)
            return undefined;
        const to = this.target();
        if (!this.transition || this.transition.to !== to) {
            if (Math.abs(current - to) < 1e-6) {
                this.transition = undefined;
                return undefined;
            }
            this.transition = { from: current, to, startMs: nowMs };
        }
        const u = easeMs > 0 ? Math.min(1, Math.max(0, (nowMs - this.transition.startMs) / easeMs)) : 1;
        const { from } = this.transition;
        if (u >= 1) {
            this.transition = undefined;
            return to;
        }
        return clampZoom(from + (to - from) * smoothstep(u));
    }
    /** A manual zoom, pinch or reset owns the zoom from now on. */
    disable() { this.enabled = false; this.transition = undefined; }
    /** A market switch starts classification afresh (the old range says nothing about the new market). */
    reset() { this.regime = 'normal'; this.changedAtMs = -Infinity; this.transition = undefined; }
}
//# sourceMappingURL=auto-zoom.js.map